/**
 * Step · Location — current city + radius + Hometown block (display-only).
 * Feb 2026 · onboarding-v2 hard-blocks the Next button until a city is set,
 * and auto-prompts browser geolocation on mount. If the user denies the
 * permission we show a soft nudge ("Turn on location for better matches")
 * and let them enter a city manually — geolocation is NOT required, only
 * the city string is.
 */
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { MapPin, ArrowRight, MapPinOff, Loader2 } from 'lucide-react';
import { COUNTRIES } from '@/lib/countries';
import { inputCls } from './shared';

export default function StepLocation({ profile, setProfile, detectLoc, onNext }) {
  // Track browser geolocation permission so we know whether to show
  // the "Turn on location for better matches" nudge. Values:
  //   'unknown' — we haven't asked yet (initial mount)
  //   'prompting' — dialog is up
  //   'granted' — user allowed, coordinates captured
  //   'denied' — user rejected; fallback to manual city entry
  //   'unsupported' — no geolocation API on this browser
  const [permission, setPermission] = useState('unknown');

  // Auto-request geolocation on first mount so users don't have to
  // hunt for the pin button. Only asks if we haven't captured lat/lng yet.
  useEffect(() => {
    if (profile.location?.lat && profile.location?.lng) {
      setPermission('granted');
      return;
    }
    if (!navigator.geolocation) {
      setPermission('unsupported');
      return;
    }
    setPermission('prompting');
    // detectLoc() from useProfileHydration handles the geolocation
    // API + reverse-geocode + toast. We wrap it here to track permission.
    try {
      navigator.geolocation.getCurrentPosition(
        () => { setPermission('granted'); detectLoc(); },
        () => { setPermission('denied'); },
        { timeout: 8000 },
      );
    } catch {
      setPermission('unsupported');
    }
    // Runs once on mount only — deliberately no deps.
  }, []);

  const cityMissing = !profile.location?.city?.trim();

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-1">Set your location</h2>
        <p className="text-zinc-500 text-sm">Your exact location is never shared.</p>
      </div>

      {/* Permission nudge — only shown when user denied browser geo. */}
      {(permission === 'denied' || permission === 'unsupported') && (
        <div
          className="bg-gradient-to-br from-[#FFFBEB] to-white border border-[#B45309]/30 rounded-2xl p-4 flex items-start gap-3"
          data-testid="location-permission-nudge"
        >
          <span className="w-9 h-9 rounded-full bg-[#B45309]/10 flex items-center justify-center flex-shrink-0">
            <MapPinOff size={16} className="text-[#B45309]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-bold text-[#0F0F10]">Turn on location for better matches</p>
            <p className="text-[11px] text-[#4B4B52] leading-relaxed">
              You&apos;ll see closer people first and get accurate distance. You can still enter your city below — location permission is optional.
            </p>
          </div>
        </div>
      )}

      {permission === 'prompting' && (
        <div className="bg-white border border-black/[0.06] rounded-2xl p-4 flex items-center gap-3" data-testid="location-permission-prompting">
          <Loader2 size={16} className="text-[#B91C1C] animate-spin" />
          <p className="text-[12px] text-[#4B4B52]">Detecting your location…</p>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <Label className="text-sm text-zinc-600 mb-1.5 block">City *</Label>
          <div className="flex gap-3">
            <Input data-testid="setup-city" placeholder="City — e.g., New York" className={`${inputCls} flex-1`}
              value={profile.location.city}
              onChange={(e) => setProfile((p) => ({ ...p, location: { ...p.location, city: e.target.value } }))} />
            <button onClick={detectLoc} data-testid="detect-location" aria-label="Detect location"
              className="w-12 h-12 rounded-xl border border-black/[0.08] flex items-center justify-center hover:bg-black/[0.02]">
              <MapPin size={18} className="text-[#EF4444]" />
            </button>
          </div>
        </div>

        <select
          data-testid="setup-country"
          className="w-full h-12 rounded-xl bg-white border border-black/[0.08] text-[#0F0F10] px-4 text-[15px] focus:outline-none focus:ring-1 focus:ring-[#B91C1C]/30 appearance-none"
          value={profile.location.country || ''}
          onChange={(e) => setProfile((p) => ({ ...p, location: { ...p.location, country: e.target.value } }))}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 14px center',
            paddingRight: 36,
          }}
        >
          <option value="">Select country…</option>
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.name}>{c.name}</option>
          ))}
        </select>

        <div>
          <label className="text-sm text-zinc-600 mb-2 block">
            Radius: <span className="text-[#EF4444] font-semibold">{profile.radius} km</span>
          </label>
          <Slider value={[profile.radius]} onValueChange={(v) => setProfile((p) => ({ ...p, radius: v[0] }))} min={5} max={200} step={5} data-testid="setup-radius" />
        </div>

        {/* Hometown (display-only, not used for discovery/distance) */}
        <div className="pt-2 border-t border-black/[0.06]" data-testid="setup-hometown-block">
          <Label className="text-sm text-zinc-600 mb-1 block">Hometown (optional)</Label>
          <p className="text-[11px] text-zinc-500 mb-3">Where you grew up. Shown on your profile — never affects who you see.</p>
          <div className="space-y-2">
            <Input
              data-testid="setup-hometown-city"
              placeholder="City — e.g., Chicago"
              maxLength={80}
              className={inputCls}
              value={profile.hometown?.city || ''}
              onChange={(e) => setProfile((p) => ({ ...p, hometown: { ...(p.hometown || {}), city: e.target.value } }))}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                data-testid="setup-hometown-state"
                placeholder="State (optional)"
                maxLength={80}
                className={inputCls}
                value={profile.hometown?.state || ''}
                onChange={(e) => setProfile((p) => ({ ...p, hometown: { ...(p.hometown || {}), state: e.target.value } }))}
              />
              <Input
                data-testid="setup-hometown-country"
                placeholder="Country (optional)"
                maxLength={80}
                className={inputCls}
                value={profile.hometown?.country || ''}
                onChange={(e) => setProfile((p) => ({ ...p, hometown: { ...(p.hometown || {}), country: e.target.value } }))}
              />
            </div>
          </div>
        </div>
      </div>

      <button onClick={onNext} data-testid="next-step-location" disabled={cityMissing}
        className="w-full brand-gradient text-white py-3.5 rounded-xl font-semibold transition-all hover:shadow-[0_0_30px_rgba(255,51,120,0.2)] disabled:opacity-30 active:scale-[0.98]">
        {cityMissing ? 'Enter your city to continue' : <>Continue <ArrowRight className="inline ml-2 w-4 h-4" /></>}
      </button>
    </div>
  );
}
